import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Lock, Mail, Phone } from "lucide-react";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { nombre: "", email: "", telefono: "", mensaje: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Mensaje enviado. Te contactaremos lo antes posible.");
      setForm(EMPTY);
    } catch {
      toast.error("No se pudo enviar el mensaje. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-gold/20 bg-white/[0.04] px-4 py-3.5 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-gold focus:ring-1 focus:ring-gold/40";

  return (
    <section id="contacto" data-testid="contact-section" className="relative border-y border-gold/10 bg-ink-2 py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-10 lg:grid-cols-2">
        <div>
          <Reveal>
            <p className="eyebrow">Contacto</p>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-white sm:text-5xl">
              Vender tu empresa es un gran paso.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-300">
              Y queremos ayudarte a darlo con confianza. Escríbenos y hablemos en una consulta inicial confidencial y
              sin compromiso.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              <a
                href="mailto:adminajgconsulting@gmail.com"
                data-testid="contact-email-link"
                className="flex items-center gap-4 border border-gold/20 bg-ink-3/60 px-6 py-5 transition-colors hover:border-gold/50"
              >
                <Mail className="h-5 w-5 shrink-0 text-gold" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Email</p>
                  <p className="mt-1 break-all text-sm text-white">adminajgconsulting@gmail.com</p>
                </div>
              </a>
              <a
                href="tel:+34654943319"
                data-testid="contact-phone-link"
                className="flex items-center gap-4 border border-gold/20 bg-ink-3/60 px-6 py-5 transition-colors hover:border-gold/50"
              >
                <Phone className="h-5 w-5 shrink-0 text-gold" />
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Teléfono</p>
                  <p className="mt-1 text-sm text-white">654 943 319</p>
                </div>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.18}>
            <div data-testid="contact-nda-promise" className="mt-6 flex gap-4 border border-gold/20 bg-gold/[0.05] px-6 py-5">
              <Lock className="h-5 w-5 shrink-0 text-gold" />
              <p className="text-sm leading-relaxed text-slate-300">
                <strong className="font-semibold text-white">Compromiso de confidencialidad.</strong> Toda la
                información que compartas se trata bajo estricta confidencialidad, antes incluso de firmar ningún
                acuerdo.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12} testid="contact-form-wrapper">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="border border-gold/15 bg-ink-3/60 p-8 md:p-10"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label htmlFor="nombre" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                  Nombre
                </label>
                <input
                  id="nombre"
                  data-testid="contact-input-nombre"
                  value={form.nombre}
                  onChange={set("nombre")}
                  required
                  placeholder="Tu nombre"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-1">
                <label htmlFor="email" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={set("email")}
                  required
                  placeholder="tu@email.com"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-6">
              <label htmlFor="telefono" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                Teléfono
              </label>
              <input
                id="telefono"
                type="tel"
                data-testid="contact-input-telefono"
                value={form.telefono}
                onChange={set("telefono")}
                placeholder="+34 600 000 000"
                className={inputCls}
              />
            </div>
            <div className="mt-6">
              <label htmlFor="mensaje" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">
                Mensaje
              </label>
              <textarea
                id="mensaje"
                rows={5}
                data-testid="contact-input-mensaje"
                value={form.mensaje}
                onChange={set("mensaje")}
                required
                placeholder="Cuéntanos brevemente sobre tu empresa y tu proyecto de venta"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all duration-300 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loading ? "Enviando..." : "Enviar mensaje"}
            </button>
            <p className="mt-6 text-xs leading-relaxed text-slate-500" data-testid="contact-privacy-note">
              Sus datos serán tratados por AJG M&amp;A CONSULTING S.L para atender su solicitud de contacto. Podrá
              ejercer sus derechos de acceso, rectificación, supresión, oposición, limitación, portabilidad o
              revocar su consentimiento, a la dirección (adminajgconsulting@gmail.com) acreditando su identidad,
              identificándose como usuario de la página de contacto de AJG y concretando su solicitud. Para más
              información sobre el tratamiento de sus datos, consulte nuestra{" "}
              <Link to="/privacidad" className="text-gold underline underline-offset-2" data-testid="contact-privacy-link">
                política de privacidad
              </Link>
              . Al enviarnos su consulta, usted consiente el tratamiento de sus datos personales.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
